import React, { useState } from "react";
import { Statistics } from "@/types/types";
import { useTranslations } from 'next-intl';
import { IoInformationCircle } from "react-icons/io5";

interface StatisticsDisplayProps {
  statistics: Statistics | null;
}

// Add new interface for comparison thresholds
interface ThresholdStatistics {
  totalScientificPublications: number | null;
  totalScientificCitations: number | null;
  hIndex: number | null;
  qScore: {
    total: number | null;
    fromArticles: number | null;
    fromBooks: number | null | string;
  };
  iScore: number | null;
  iScoreWOS: number | null;
  impactFactor: {
    total: number | null;
    relative: number | null;
  };
  publicationsWithIF: number | null;
  singleAuthorPublicationsWithIF: number | null;
}

interface ThresholdLevels {
  phd: ThresholdStatistics;
  habilitation: ThresholdStatistics; 
  levelA: ThresholdStatistics;
  levelB: ThresholdStatistics;
}

const STORAGE_KEY = 'statistics-comparison-level';

const StatisticsDisplay: React.FC<StatisticsDisplayProps> = ({ statistics }) => {
  const t = useTranslations('statistics');
  if (!statistics) return null;
  const [selectedLevel, setSelectedLevel] = useState<keyof ThresholdLevels>(() => {
    // Initialize from localStorage or default to 'habilitation'
    const savedLevel = localStorage.getItem(STORAGE_KEY);
    return (savedLevel as keyof ThresholdLevels) || 'habilitation';
  });

  // Handle level selection with localStorage update
  const handleLevelSelect = (level: keyof ThresholdLevels) => {
    setSelectedLevel(level);
    localStorage.setItem(STORAGE_KEY, level);
  };


  // Temporary mock data for thresholds
  const thresholds: ThresholdLevels = {
    phd: {
      qScore: { total: 2, fromArticles: null, fromBooks: null },
      iScore: null,
      iScoreWOS: null,
      impactFactor: { total: null, relative: null },
      publicationsWithIF: 1,
      singleAuthorPublicationsWithIF: null,
      hIndex: null,
      totalScientificCitations: null,
      totalScientificPublications: 4,
    },
    habilitation: {
      qScore: { total: 6, fromArticles: null, fromBooks: null },
      iScore: 45,
      iScoreWOS: 23,
      impactFactor: { total: null, relative: null },
      publicationsWithIF: 3,
      singleAuthorPublicationsWithIF: 1,
      hIndex: null,
      totalScientificCitations: null,
      totalScientificPublications: null,
    },
    levelA: {
      qScore: { total: 12, fromArticles: 6, fromBooks: "3 max" },
      iScore: 90,
      iScoreWOS: 45,
      impactFactor: { total: null, relative: 3 },
      publicationsWithIF: 6,
      singleAuthorPublicationsWithIF: 2,
      hIndex: 5,
      totalScientificCitations: null,
      totalScientificPublications: null,
    },
    levelB: {
      qScore: { total: 16, fromArticles: 8, fromBooks: "3 max" },
      iScore: 200,
      iScoreWOS: 100,
      impactFactor: { total: null, relative: 4 },
      publicationsWithIF: 8,
      singleAuthorPublicationsWithIF: 2,
      hIndex: 7,
      totalScientificCitations: null,
      totalScientificPublications: null,
    },
  };

  

  // Updated helper function to handle null thresholds
  const getComparisonColor = (current: number | null, threshold: number | null | string): string => {
    if (threshold === null || current === null || typeof threshold === 'string') return 'text-gray-600';
    if (Number(current) >= Number(threshold)) return 'bg-green-100 text-green-800';
    return 'bg-red-100 text-red-800';
  };

  // Updated helper function to get column highlight style
  const getColumnStyle = (level: keyof ThresholdLevels): string => {
    return selectedLevel === level ? 'bg-[#dddddd]' : '';
  };

  // Updated helper function to get header style with fixed width to prevent layout shift
  const getHeaderStyle = (level: keyof ThresholdLevels): string => {
    return `cursor-pointer transition-all duration-200 inline-block font-medium ${
      selectedLevel === level 
        ? 'text-gray-900' 
        : 'text-gray-500 hover:text-gray-700'
    }`;
  };

  const getValue = (obj: any, path: string): number | null | string => {
    const parts = path.split('.');
    let value = obj;
    for (const part of parts) {
      if (value === null || value === undefined) return null;
      value = value[part];
    }
    return value;
  };

  return (
    <div className="bg-white/90 border-2 shadow-sm border-gray-300/50 rounded-xl p-2 sm:p-3 md:p-5 mb-7 mt-2">
      <div className="text-center mb-3.5 text-xs sm:text-sm text-gray-600 flex flex-wrap items-center justify-center gap-1 sm:gap-2 px-2 sm:px-0">
        <IoInformationCircle className="text-[#5c6cff] text-base sm:text-lg flex-shrink-0" />
        {t('clickToCompare')}
      </div>

      {/* Responsive table container with horizontal scroll on mobile */}
      <div className="w-full overflow-x-auto md:overflow-hidden -mx-1.5 sm:mx-0">
        <div className="min-w-[640px] w-full px-1.5 sm:px-0">
          <table className="w-full divide-y divide-gray-200">
            <thead>
              <tr>
                <th className="w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                </th>
                <th className="w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('headers.current')}
                </th>
                {(['phd', 'habilitation', 'levelA', 'levelB'] as const).map((level) => (
                  <th 
                    key={level}
                    className={`w-1/6 px-2 py-2 text-left text-[10px] sm:text-xs uppercase tracking-wider transition-colors duration-200 ${getColumnStyle(level)}`}
                  >
                    <span 
                      onClick={() => handleLevelSelect(level)} 
                      className={getHeaderStyle(level)}
                    >
                      {t(`headers.${level}`)}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {[
                { 
                  key: 'qScore.total', 
                  label: t('stats.qscore.total'),
                  getValue: (stats: any) => stats.qScore?.total?.toFixed(3)
                },
                { 
                  key: 'qScore.fromArticles', 
                  label: t('stats.qscore.articles'),
                  getValue: (stats: any) => stats.qScore?.fromArticles?.toFixed(3)
                },
                { 
                  key: 'qScore.fromBooks', 
                  label: t('stats.qscore.books'),
                  getValue: (stats: any) => stats.qScore?.fromBooks?.toFixed(3)
                },
                { key: 'iScore', label: t('stats.iscore.total') },
                { key: 'iScoreWOS', label: t('stats.iscore.wos') },
                { 
                  key: 'impactFactor.total', 
                  label: t('stats.impactfactor.total'),
                  getValue: (stats: any) => stats.impactFactor?.total?.toFixed(3)
                },
                { 
                  key: 'impactFactor.relative', 
                  label: t('stats.impactfactor.relative'),
                  getValue: (stats: any) => stats.impactFactor?.relative?.toFixed(3)
                },
                { key: 'publicationsWithIF', label: t('stats.impactfactor.publications') },
                { key: 'singleAuthorPublicationsWithIF', label: t('stats.impactfactor.singleAuthor') },
                { key: 'hIndex', label: t('stats.totals.hIndex') },
                { key: 'totalScientificCitations', label: t('stats.totals.totalScientificCitations') },
                { key: 'totalScientificPublications', label: t('stats.totals.scientificPublications') }
              ].map((metric) => (
                <tr key={metric.key} className="hover:bg-gray-50">
                  <td className="px-2 py-2 whitespace-normal text-[10px] sm:text-sm font-medium text-gray-900">
                    <div className="group relative inline-flex items-center">
                      <span className="hover:text-gray-600">{metric.label}</span>
                      <div 
                        role="tooltip" 
                        className="absolute left-0 sm:left-full mt-2 sm:mt-0 sm:ml-2 w-48 sm:w-64 z-10 invisible group-hover:visible bg-gray-900 text-white text-[10px] sm:text-sm rounded-lg py-2 px-3 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      >
                        <p className="whitespace-normal break-words">
                          {t(`tooltips.${metric.key}`)}
                        </p>
                        <div className="absolute w-2 h-2 bg-gray-900 transform rotate-45 left-4 -top-1 sm:left-[-4px] sm:top-1/2 sm:-translate-y-1/2" />
                      </div>
                    </div>
                  </td>
                  <td className={`px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm font-bold ${
                    getComparisonColor(
                      metric.getValue ? metric.getValue(statistics) : getValue(statistics, metric.key),
                      getValue(thresholds[selectedLevel], metric.key)
                    )}`}
                  >
                    {metric.getValue ? metric.getValue(statistics) : getValue(statistics, metric.key)}
                  </td>
                  <td className={`px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm text-gray-600 ${getColumnStyle('phd')}`}>
                    {getValue(thresholds.phd, metric.key) ?? '-'}
                  </td>
                  <td className={`px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm text-gray-600 ${getColumnStyle('habilitation')}`}>
                    {getValue(thresholds.habilitation, metric.key) ?? '-'}
                  </td>
                  <td className={`px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm text-gray-600 ${getColumnStyle('levelA')}`}>
                    {getValue(thresholds.levelA, metric.key) ?? '-'}
                  </td>
                  <td className={`px-2 py-2 whitespace-nowrap text-[10px] sm:text-sm text-gray-600 ${getColumnStyle('levelB')}`}>
                    {getValue(thresholds.levelB, metric.key) ?? '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StatisticsDisplay;
